import { inject, Injectable, signal } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { race } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

import { InterviewResultsActions } from '@core/interview-results-data';
import {
  InterviewKind,
  QuickEvalOperationType,
  QuickEvaluationNotificationService,
} from '@core/interview-results-legacy';
import type { InterviewResult } from '@core/interview-results-types';
import { InterviewTemplateDetailsActions } from '@core/interview-templates-data';
import type { InterviewTemplateDetail } from '@core/interview-templates-types';
import { generateUuid } from '@core/utils-legacy';

@Injectable()
export class EvaluationPanelActionsService {
  private readonly quickEvaluationNotificationService = inject(QuickEvaluationNotificationService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  public readonly isSubmitting = signal(false);
  public readonly isLoading = signal(false);

  public fetchInterviewResult(resultId: number): Observable<InterviewResult> {
    const result$ = this.actions$.pipe(
      ofType(InterviewResultsActions.fetchItemSuccess),
      map(({ payload }) => payload.data.interviewResult),
      filter(result => result.id === resultId),
      take(1),
      tap(() => this.isLoading.set(false)),
    );

    this.isLoading.set(true);
    this.store.dispatch(InterviewResultsActions.fetchItem({ payload: { id: resultId } }));

    return result$;
  }

  public fetchTemplate(
    templateId: number,
    jobPositionId?: number | null,
  ): Observable<InterviewTemplateDetail> {
    const template$ = this.actions$.pipe(
      ofType(InterviewTemplateDetailsActions.fetchOneSuccess),
      map(({ payload }) => payload.data),
      filter(template => template.id === templateId),
      take(1),
      tap(() => this.isLoading.set(false)),
    );

    this.isLoading.set(true);
    this.store.dispatch(
      InterviewTemplateDetailsActions.fetchOne({ payload: { id: templateId, jobPositionId } }),
    );

    return template$;
  }

  public createQuickEvaluation(
    candidateId: number,
    value: Partial<InterviewResult>,
  ): Observable<boolean> {
    return this.createEvaluation(candidateId, value, InterviewKind.QUICK);
  }

  public createTemplateEvaluation(
    candidateId: number,
    value: Partial<InterviewResult>,
  ): Observable<boolean> {
    return this.createEvaluation(candidateId, value, InterviewKind.TEMPLATE);
  }

  public updateQuickEvaluation(
    resultId: number,
    value: Partial<InterviewResult>,
  ): Observable<boolean> {
    return this.updateEvaluation(resultId, value, InterviewKind.QUICK);
  }

  public updateTemplateEvaluation(
    resultId: number,
    value: Partial<InterviewResult>,
  ): Observable<boolean> {
    return this.updateEvaluation(resultId, value, InterviewKind.TEMPLATE);
  }

  private updateEvaluation(
    resultId: number,
    value: Partial<InterviewResult>,
    kind: InterviewKind,
  ): Observable<boolean> {
    const result$ = race(
      this.actions$.pipe(
        ofType(InterviewResultsActions.updateSuccess),
        filter(({ payload }) => payload.data.id === resultId),
        tap(() =>
          this.quickEvaluationNotificationService.success({
            operationType: QuickEvalOperationType.UPDATE,
          }),
        ),
        map(() => true),
      ),
      this.actions$.pipe(
        ofType(InterviewResultsActions.updateFailed),
        map(() => false),
      ),
    ).pipe(
      take(1),
      tap(() => this.isSubmitting.set(false)),
    );

    this.isSubmitting.set(true);
    this.store.dispatch(
      InterviewResultsActions.update({
        payload: {
          data: {
            id: resultId,
            value: { interviewResult: { ...value, kind } },
          },
        },
      }),
    );

    return result$;
  }

  private createEvaluation(
    candidateId: number,
    value: Partial<InterviewResult>,
    kind: InterviewKind,
  ): Observable<boolean> {
    const correlationId = generateUuid();

    const result$ = race(
      this.actions$.pipe(
        ofType(InterviewResultsActions.createOnePessimisticSuccess),
        filter(({ payload }) => payload.correlationId === correlationId),
        tap(({ payload }) =>
          this.quickEvaluationNotificationService.success({
            operationType: QuickEvalOperationType.ADD,
            rating: payload.data.rating,
          }),
        ),
        map(() => true),
      ),
      this.actions$.pipe(
        ofType(InterviewResultsActions.createOnePessimisticFailed),
        map(() => false),
      ),
    ).pipe(
      take(1),
      tap(() => this.isSubmitting.set(false)),
    );

    this.isSubmitting.set(true);
    this.store.dispatch(
      InterviewResultsActions.createOnePessimistic({
        payload: {
          data: {
            value: { ...value, kind },
            candidateId,
          },
          correlationId,
        },
      }),
    );

    return result$;
  }
}
